import type { ProjectMeta, Signatory } from './types';

/**
 * THE PEOPLE WHO SIGN WORK OFF, AND THE ONES THIS PROJECT HAS EVER KNOWN.
 *
 * Kept out of the screen because two of these three rules are about what NOT to
 * lose, and rules about not losing things are the ones that get quietly dropped
 * when a component is rewritten.
 */

/** Nothing to sign with: no name, or a name and no position. Both are asked. */
/**
 * ONE EMPTY PANEL, SHARED — AND IT IS NOT TIDINESS.
 *
 * `useApp((s) => s.meta.signatories ?? [])` reads correctly and is a hang. The
 * store is read through `useSyncExternalStore`, which decides whether anything
 * changed by comparing the selector's RESULT to the last one BY REFERENCE. A
 * fresh `[]` is a different reference every time it is built, so a board whose
 * signatories have not arrived yet answers "changed" to every check, for ever:
 * React re-renders, re-reads, sees a new array, and re-renders — until it gives
 * up with error #185, maximum update depth.
 *
 * It killed the Releases screen on 30 August 2026, and it was on live at the
 * time. Selecting the stored value and defaulting OUTSIDE the selector is the
 * fix; a constant is what makes that possible where a default is still wanted
 * inside one.
 */
export const NOBODY: readonly Signatory[] = Object.freeze([]);

export const isComplete = (s: Signatory) => s.name.trim() !== '' && s.position.trim() !== '';

/** The same person, however either half was capitalised or spaced. */
const sameAs = (a: Signatory, b: Signatory) =>
  a.name.trim().toLowerCase() === b.name.trim().toLowerCase() &&
  a.position.trim().toLowerCase() === b.position.trim().toLowerCase();

/**
 * EVERYBODY THIS PROJECT HAS EVER PUT ON ITS PANEL, plus whoever is on it now.
 *
 * Taking a one-off approver off the panel must not erase that they existed —
 * an auditor asking who signed release 12 needs the name to still mean
 * somebody, and the next person to add them should PICK them rather than type
 * the name again slightly differently. Two spellings of one person is two
 * people as far as any record is concerned.
 *
 * Only complete entries are remembered: half-typed rows are not people yet.
 */
export function rememberAll(known: Signatory[], panel: Signatory[]): Signatory[] {
  const out = [...known];
  for (const person of panel) {
    if (!isComplete(person)) continue;
    if (out.some((seen) => sameAs(seen, person))) continue;
    out.push({
      name: person.name.trim(),
      position: person.position.trim(),
      /* CARRIED, or somebody picked off the roster comes back as a name with no
         way to sign their own row — which is the fault this all exists to fix,
         reintroduced by a chip. */
      ...(person.email?.trim() ? { email: person.email.trim() } : {}),
    });
  }
  return out;
}

/** The remembered people who are not on the panel right now — the ones worth
 *  offering back, since the rest are already there. */
export const notOnThePanel = (known: Signatory[], panel: Signatory[]) =>
  known.filter((person) => !panel.some((on) => sameAs(on, person)));

/**
 * WHO SIGNS A CHARTER — its own list, not the board's delivery panel.
 *
 * A charter is the instrument that AUTHORISES a project. It is signed by its
 * sponsor, and whoever else must approve it — a steering chair, the PMO,
 * finance. It is emphatically not signed by the developer → tester → approver →
 * owner chain a release or a sprint runs through, so a charter does not read the
 * board's sign-off policy at all. The manager names its signatories when they
 * send it, seeded with the sponsor from "About this project".
 *
 * A charter goes to REAL people who receive a REAL signing request, so every
 * signatory needs a name, a role and an email: a nameless or unreachable row
 * would be written and then unable to sign, which is the fault the delivery
 * panel's `isComplete` rule exists to prevent, re-created on a different screen.
 * These three helpers are pure so the rule is proven once (test-who-signs-the-
 * charter) and the store and the screen both read the same answer.
 */

/** The one seeded row: the sponsor from About, split into name and role on its
 *  first comma ("Pat Moore, Operations Director" → name + role), with an empty
 *  email for the manager to fill. An empty sponsor seeds one blank row to type
 *  into. Never seeds an email — About holds no address for the sponsor. */
export function seedCharterSignatories(sponsor?: string): Signatory[] {
  const s = (sponsor ?? '').trim();
  if (!s) return [{ name: '', position: '', email: '' }];
  const [name, ...rest] = s.split(',');
  return [{ name: name.trim(), position: rest.join(',').trim() || 'Sponsor', email: '' }];
}

/** A trimmed, de-blanked copy of the rows — the shape written to the sign-off.
 *  Wholly empty rows (a spare the manager never filled) are dropped. */
export function charterSignoffRows(people: Signatory[]): Signatory[] {
  return people
    .map((p) => ({
      name: p.name.trim(),
      position: p.position.trim(),
      ...(p.email?.trim() ? { email: p.email.trim().toLowerCase() } : {}),
    }))
    .filter((r) => r.name || r.position || r.email);
}

/** The reason a charter's signatory list cannot be sent, or '' when it can.
 *  A sentence, not a raise — the same voice the delivery panel refuses in. */
export function charterSignoffProblem(people: Signatory[]): string {
  const rows = charterSignoffRows(people);
  if (rows.length === 0) return 'Add at least one person to sign the charter.';
  if (rows.some((r) => !r.name || !r.position))
    return 'Every charter signatory needs a name and a role.';
  if (rows.some((r) => !r.email))
    return 'Every charter signatory needs an email, so the signing request can reach them.';
  if (rows.some((r) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.email!)))
    return 'One of the charter signatory emails is not a full email address.';
  const seen = new Set<string>();
  for (const r of rows) {
    if (seen.has(r.email!)) return `${r.email} is listed twice — each person signs once.`;
    seen.add(r.email!);
  }
  return '';
}

/**
 * THE PANEL AS IT IS ACTUALLY ASKED — the project owner first, then everybody
 * typed under them.
 *
 * The owner used to be named on the board and asked on nothing. The panel was a
 * separate list, so the person who commissioned the software did not get to
 * approve the software they commissioned, and the only way onto the sheet was
 * to type their name a second time underneath — which is two people as far as
 * any record is concerned.
 *
 * FIRST, NOT LAST. Order on a sign-off sheet is seniority, and the owner
 * outranks the panel they appointed.
 *
 * DERIVED RATHER THAN COPIED IN. Writing the owner into `signatories` would
 * make the panel disagree with itself the moment their title changed, and there
 * would be no way to tell the seeded row from one somebody typed.
 *
 * Used everywhere a panel is frozen, so what Settings shows is exactly what a
 * release or a document is sent to. Three call sites had to agree by hand
 * before this existed; one of them did not.
 */
export function thePanel(
  meta: Pick<
    ProjectMeta,
    'projectOwnerName' | 'projectOwnerPosition' | 'projectOwnerEmail' | 'ownerSignsOff' | 'signatories'
  >
): Signatory[] {
  const named = (meta.signatories ?? []).filter(isComplete);
  if (meta.ownerSignsOff === false) return named;

  const owner: Signatory = {
    name: (meta.projectOwnerName ?? '').trim(),
    position: (meta.projectOwnerPosition ?? '').trim(),
    email: (meta.projectOwnerEmail ?? '').trim(),
  };
  /* Half a name is not a person yet — the same rule the panel itself uses. */
  if (!isComplete(owner)) return named;
  /* Typed in underneath as well, on a board set up before this: one row, and
     the one already in the list keeps its place rather than jumping to the
     top. Somebody put it where it is. */
  if (named.some((who) => sameAs(who, owner))) return named;

  return [owner, ...named];
}

/** The owner as a signatory, or null if the board has not named one. Ignores
 *  whether they are switched on — this is "who is the owner", not "who signs". */
export function theOwner(
  meta: Pick<ProjectMeta, 'projectOwnerName' | 'projectOwnerPosition' | 'projectOwnerEmail'>
): Signatory | null {
  const who: Signatory = {
    name: (meta.projectOwnerName ?? '').trim(),
    position: (meta.projectOwnerPosition ?? '').trim(),
    email: (meta.projectOwnerEmail ?? '').trim(),
  };
  return isComplete(who) ? who : null;
}

/**
 * THE REMEMBERED PEOPLE WORTH OFFERING BACK AS A PRESS.
 *
 * Everybody this project has ever asked who is not being asked now — minus the
 * project owner, always.
 *
 * WHY THE OWNER IS EXCLUDED EVEN WHEN THEY ARE OFF THE PANEL. Their chip would
 * add them as an ORDINARY TYPED ROW, leaving the board saying two things at
 * once: that the owner does not approve work, and that somebody with the
 * owner's exact name and position does. The walk did precisely that and then
 * sent a document to a panel with the owner at the bottom of it.
 *
 * They have a row of their own and a sentence of their own to get back onto it.
 */
export function offerBack(
  meta: Pick<
    ProjectMeta,
    | 'projectOwnerName'
    | 'projectOwnerPosition'
    | 'projectOwnerEmail'
    | 'ownerSignsOff'
    | 'signatories'
    | 'signatoriesKnown'
  >
): Signatory[] {
  const owner = theOwner(meta);
  return notOnThePanel(meta.signatoriesKnown ?? [], thePanel(meta)).filter(
    (who) => !(owner && sameAs(owner, who))
  );
}
